/* ============================================================
   HERO CHEVRON PATTERN — 3D
   ------------------------------------------------------------
   The chevron polygon is lifted straight out of the brand SVG and
   extruded + instanced, so the 3D field is the same artwork with
   real depth rather than a lookalike.

   Idle:  the whole field flows left-to-right along the pattern's
          own repeat vector, wrapping seamlessly, while a liquid wave
          runs through the ribbons themselves — displaced per-vertex
          in the shader, with normals recomputed analytically so the
          light actually ripples instead of the shapes bobbing.
   Hover: the whole field leans toward the cursor (subtle), and the
          key light drags across the corrugation with it.
   Falls back to the flat inline SVG if WebGL is unavailable, and to
   a single static frame under prefers-reduced-motion.

   Scoped via [data-me-hero-pattern] rather than a bare class, so it
   survives the Theme Editor re-rendering the section (shopify:section:load).
   ============================================================ */
import * as THREE from 'three';

const CHEVRON = [[[283.12,71.92],[-295.33,135.31],[-296.95,139.28],[281.51,76.04]],[[281.51,76.04],[156.2,402.55],[166.79,402.55],[292.68,74.72]],[[283.12,71.92],[281.51,76.04],[292.68,74.72],[294.15,70.74]]];

function initHeroPattern(host) {
  if (!host || host.dataset.meHeroInit === '1') return;

  const canvas = host.querySelector('.me-hero__canvas');
  const hero = host.closest('.me-hero');
  if (!canvas || !hero) return;

  host.dataset.meHeroInit = '1';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
  } catch (e) {
    return;                       // no WebGL — the SVG stays visible
  }
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));

  const VB_W = 430.348, VB_H = 242.089, FOV = 24, DEPTH = 5.0;

  // The artwork repeats every (19.414, 10.4425) in three-space, so translating
  // the field by exactly that vector maps it onto itself — that is what makes
  // the travel loop seamless. Anything else would visibly jump on wrap.
  const RPT_X = 19.414, RPT_Y = 10.4425;
  const COUNT = 76, FIRST = -26;      // instances along the repeat (covers ultrawide)
  const SPEED = 0.32;                 // repeats per second, left -> right
  const THICK = 1.0;                  // matches the source SVG's own ribbon width exactly

  const scene  = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(FOV, 1, 1, 6000);

  // ---- geometry ------------------------------------------------------------
  // One chevron ships as 3 convex quads (two arms + apex cap). The original SVG
  // outline is a thin self-touching ribbon that triangulates into a filled
  // wedge, so the stroke is rebuilt from quads. That single chevron is then
  // instanced along the repeat vector — 3 draw calls for the whole field.
  const material = new THREE.MeshStandardMaterial({ color: 0xE65300, roughness: 0.48, metalness: 0.0, side: THREE.DoubleSide });

  // Two requirements pull against each other: the liquid wave needs vertices
  // along the ribbon to bend through (4 corners can only tilt flat), and the
  // arrows need side walls or they lose all edge definition and go soft.
  //
  // Built by hand rather than via ExtrudeGeometry: triangulating a thin
  // 100-point outline produces slivers and coplanar overlaps that speckle.
  // Here the face is an explicit strip and each wall is an explicit quad run,
  // so the topology is exact. side1 = q0->q1, side2 = q3->q2.
  function ribbon(q) {
    const A = q[0], B = q[1], C = q[2], D = q[3];
    const len  = Math.hypot(B[0] - A[0], B[1] - A[1]);
    const segs = Math.max(2, Math.min(120, Math.round(len / 10)));

    const s1 = [], s2 = [];
    for (let i = 0; i <= segs; i++) {
      const t = i / segs;
      const p1x = A[0] + (B[0] - A[0]) * t, p1y = -(A[1] + (B[1] - A[1]) * t);
      const p2x = D[0] + (C[0] - D[0]) * t, p2y = -(D[1] + (C[1] - D[1]) * t);
      // push both edges outward from their shared midline to thicken the arrow
      // without moving its centerline (keeps the repeat-vector flow untouched)
      const mx = (p1x + p2x) / 2, my = (p1y + p2y) / 2;
      s1.push([mx + (p1x - mx) * THICK, my + (p1y - my) * THICK]);
      s2.push([mx + (p2x - mx) * THICK, my + (p2y - my) * THICK]);
    }

    const pos = [], nor = [], idx = [];
    const strip = function (rowA, rowB, normals) {
      const base = pos.length / 3;
      for (let i = 0; i <= segs; i++) {
        pos.push(rowA[i][0], rowA[i][1], rowA[i][2]);
        nor.push(normals[i][0], normals[i][1], normals[i][2]);
        pos.push(rowB[i][0], rowB[i][1], rowB[i][2]);
        nor.push(normals[i][0], normals[i][1], normals[i][2]);
      }
      for (let i = 0; i < segs; i++) {
        const a = base + i * 2;                     // CCW = front-facing in three
        idx.push(a, a + 2, a + 1, a + 1, a + 2, a + 3);
      }
    };

    // front face
    const f1 = [], f2 = [], fn = [];
    for (let i = 0; i <= segs; i++) {
      f1.push([s1[i][0], s1[i][1], DEPTH]);
      f2.push([s2[i][0], s2[i][1], DEPTH]);
      fn.push([0, 0, 1]);
    }
    strip(f1, f2, fn);

    // side walls — outward normal per sample, so the edge catches light
    const wall = function (edge, other) {
      const lo = [], hi = [], nn = [];
      for (let i = 0; i <= segs; i++) {
        const a = edge[Math.max(i - 1, 0)], b = edge[Math.min(i + 1, segs)];
        let tx = b[0] - a[0], ty = b[1] - a[1];
        const tl = Math.hypot(tx, ty) || 1; tx /= tl; ty /= tl;
        let nx = -ty, ny = tx;                       // perpendicular in-plane
        const wx = other[i][0] - edge[i][0], wy = other[i][1] - edge[i][1];
        if (nx * wx + ny * wy > 0) { nx = -nx; ny = -ny; }   // point away from the ribbon
        lo.push([edge[i][0], edge[i][1], 0]);
        hi.push([edge[i][0], edge[i][1], DEPTH]);
        nn.push([nx, ny, 0]);
      }
      strip(lo, hi, nn);
    };
    wall(s1, s2);
    wall(s2, s1);

    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('normal',   new THREE.Float32BufferAttribute(nor, 3));
    g.setIndex(idx);
    return g;
  }

  const group = new THREE.Group();
  const meshes = CHEVRON.map(function (q) {
    const im = new THREE.InstancedMesh(ribbon(q), material, COUNT);
    im.frustumCulled = false;
    im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    group.add(im);
    return im;
  });

  // ---- liquid displacement -------------------------------------------------
  // Three crossing sines in world space. The gradient is the analytic
  // derivative of that sum, so the normal is exact and the light ripples
  // correctly rather than the surface just moving under flat shading.
  const uTime = { value: 0 };
  const uAmp  = { value: 12.0 };

  material.onBeforeCompile = function (shader) {
    shader.uniforms.uTime = uTime;
    shader.uniforms.uAmp  = uAmp;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', [
        '#include <common>',
        'uniform float uTime;',
        'uniform float uAmp;',
        'const vec3 A = vec3(0.55, 0.45, 0.35);',      // amplitudes
        'const vec3 K = vec3(0.0215, 0.0288, 0.0146);', // spatial frequencies
        'const vec3 S = vec3(0.85, -0.62, 1.15);',      // temporal speeds
        'float meWave(vec2 p) {',
        '  return A.x * sin(p.x * K.x + uTime * S.x)',
        '       + A.y * sin(p.y * K.y + uTime * S.y)',
        '       + A.z * sin((p.x + p.y) * K.z + uTime * S.z);',
        '}',
        'vec2 meGrad(vec2 p) {',
        '  float c3 = A.z * K.z * cos((p.x + p.y) * K.z + uTime * S.z);',
        '  return vec2(A.x * K.x * cos(p.x * K.x + uTime * S.x) + c3,',
        '              A.y * K.y * cos(p.y * K.y + uTime * S.y) + c3);',
        '}'
      ].join('\n'))
      // instance matrices are pure translations, so world xy is position + column 3
      .replace('#include <beginnormal_vertex>', [
        '#include <beginnormal_vertex>',
        'vec2 meP = position.xy + vec2(instanceMatrix[3][0], instanceMatrix[3][1]);',
        'vec2 meG = meGrad(meP) * uAmp;',
        // scale by |n.z| so faces get the full wave and side walls keep theirs
        'objectNormal = normalize(objectNormal + vec3(-meG.x, -meG.y, 0.0) * abs(objectNormal.z));'
      ].join('\n'))
      .replace('#include <begin_vertex>', [
        '#include <begin_vertex>',
        'transformed.z += meWave(meP) * uAmp;'
      ].join('\n'));
  };

  // Place every instance for a given moment. `scroll` walks 0->1 across one
  // repeat; on wrap, instance i lands exactly where instance i+1 was, so the
  // set of positions is unchanged and the loop is invisible. The Z wave is
  // keyed off the continuous position `f` (not the instance index) for the
  // same reason.
  const dummy = new THREE.Object3D();
  function layout(t) {
    const scroll = (t * SPEED) % 1;
    for (let i = 0; i < COUNT; i++) {
      const f = FIRST + i + scroll;
      dummy.position.set(RPT_X * f, RPT_Y * f, 0);
      dummy.updateMatrix();
      for (let m = 0; m < meshes.length; m++) meshes[m].setMatrixAt(i, dummy.matrix);
    }
    for (let m = 0; m < meshes.length; m++) meshes[m].instanceMatrix.needsUpdate = true;
  }

  group.position.set(-VB_W / 2, VB_H / 2, 0);     // centre the viewBox on the origin
  const field = new THREE.Group();
  field.add(group);
  scene.add(field);

  // ---- light: raking from upper-left so the extrusions read ------------------
  // ambient carries the flat colour; the key mostly grazes the extruded sides
  // three r155+ is physically-correct: irradiance is divided by PI, so these
  // intensities are PI-scaled to land a camera-facing chevron on its true
  // #E65300. Side faces miss the key light and darken, which is the depth cue.
  // Ambient ignores normals, so a wave shows only in the directional share.
  // Weighted toward the key (65% of front-face light) to make the ripple read,
  // while still summing to ~1.0 on a camera-facing face to hold #E65300.
  scene.add(new THREE.AmbientLight(0xffffff, 1.30));
  const key = new THREE.DirectionalLight(0xfff3e8, 1.83);
  key.position.set(-150, 260, 620);
  scene.add(key);
  const rim = new THREE.DirectionalLight(0xffb070, 0.30);
  rim.position.set(340, -200, 320);
  scene.add(rim);

  // ---- cover-fit the viewBox, same as preserveAspectRatio="slice" -----------
  function resize() {
    const w = host.clientWidth, h = host.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    const visibleH = (camera.aspect > VB_W / VB_H) ? VB_W / camera.aspect : VB_H;
    camera.position.z = (visibleH / 2) / Math.tan(THREE.MathUtils.degToRad(FOV / 2));
    camera.updateProjectionMatrix();
  }
  resize();
  new ResizeObserver(resize).observe(host);

  // ---- pointer parallax -----------------------------------------------------
  // Tilt alone barely reads on a repeating field, so hover also drags the key
  // light across the corrugation — that highlight shift is what the eye catches.
  const BASE_RX = -0.030, BASE_RY = 0.044;
  const TILT_X = 0.075, TILT_Y = 0.110;        // radians at full deflection (~4-6 deg)
  const LIGHT_X = 520, LIGHT_Y = 430;          // how far the key light travels
  const KEY_X = -150, KEY_Y = 260;             // its resting position
  let targetX = 0, targetY = 0, curX = 0, curY = 0;

  if (!reduceMotion) {
    hero.addEventListener('pointermove', function (e) {
      const r = hero.getBoundingClientRect();
      targetX = ((e.clientX - r.left) / r.width  - 0.5) * 2;
      targetY = ((e.clientY - r.top)  / r.height - 0.5) * 2;
    });
    hero.addEventListener('pointerleave', function () { targetX = 0; targetY = 0; });
  }

  // ---- loop -----------------------------------------------------------------
  const clock = new THREE.Clock();
  let running = true, frame = 0, paused = false;

  function render() {
    const t = clock.getElapsedTime();

    if (!reduceMotion && !paused) {
      uTime.value = t;
      layout(t);
      curX += (targetX - curX) * 0.055;
      curY += (targetY - curY) * 0.055;
    }

    field.rotation.x = BASE_RX + curY * TILT_X;
    field.rotation.y = BASE_RY + curX * TILT_Y;
    key.position.x = KEY_X + curX * LIGHT_X;
    key.position.y = KEY_Y - curY * LIGHT_Y;

    renderer.render(scene, camera);
  }

  function loop() {
    if (!running) return;
    frame = requestAnimationFrame(loop);
    render();
  }

  if (reduceMotion) {
    layout(0);
    render();                                   // one static 3D frame, no animation
  } else {
    // only burn frames while the hero is actually on screen
    new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting && !running) { running = true; clock.getDelta(); loop(); }
        else if (!en.isIntersecting && running) { running = false; cancelAnimationFrame(frame); }
      });
    }, { threshold: 0 }).observe(hero);
    loop();
  }

  window.MEHero = {
    amp: function (v) { if (v !== undefined) uAmp.value = v; return uAmp.value; },
    pause: function (v) { paused = (v === undefined) ? !paused : !!v; return paused; },
    pointer: function () { return { x: curX, y: curY }; },
    rotation: function () { return { x: field.rotation.x, y: field.rotation.y }; },
    light: function () { return { x: key.position.x, y: key.position.y }; }
  };

  canvas.addEventListener('webglcontextlost', function (e) {
    e.preventDefault(); running = false; cancelAnimationFrame(frame);
    host.classList.remove('is-3d');             // fall back to the SVG
  });

  host.classList.add('is-3d');
}

function initAll() {
  Array.prototype.forEach.call(document.querySelectorAll('[data-me-hero-pattern]'), initHeroPattern);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}

// re-init when the section is reloaded inside the Theme Editor
document.addEventListener('shopify:section:load', function (e) {
  const host = e.target.querySelector('[data-me-hero-pattern]');
  if (host) initHeroPattern(host);
});
